export const STORAGE_KEY = 'stage4eu_chatbot_session';
export const STORAGE_VERSION = 5;

export const EMPTY_CONVERSATION_STATE = {
  previous_response_mode: '',
  pending_country: '',
  last_offer_ids: [],
  selected_offer_id: 0,
  informational_topic: '',
  informational_countries: [],
  informational_aspects: [],
};

export const EMPTY_FILTERS = {
  intent: 'unclear',
  paesi: [],
  paesi_ids: [],
  aree: [],
  aree_ids: [],
  keywords: [],
  confidence: 0,
};

export const BOT_WELCOME_TEXT =
  'Ciao, sono Stage4eu Assistant, un sistema di intelligenza artificiale. Posso aiutarti a usare il portale e a cercare uno stage in Europa. Le mie risposte possono contenere errori: verifica sempre le informazioni importanti.';

const TRACKED_RESPONSE_MODES = new Set([
  'off_topic_blocked',
  'anything_topic_blocked',
  'application_material_blocked',
  'application_material_advice',
  'country_disambiguation',
  'anythingllm_informational',
  'stage_search_no_offers',
  'stage_search_results',
  'stage_search_offer_selected',
]);

const DEFINITIVE_CHAT_CLIENT_ERRORS = new Set([400, 405, 413, 429]);
const INFORMATIONAL_RESPONSE_MODES = new Set([
  'application_material_advice',
  'anythingllm_informational',
]);
const INFORMATIONAL_TOPICS = new Set([
  'application_material',
  'country_guide',
  'organizations',
  'qualifications',
  'europass',
  'stage_basics',
  'faq',
]);

export const createWelcomeMessage = () => ({
  id: 'welcome',
  role: 'bot',
  type: 'text',
  text: BOT_WELCOME_TEXT,
});

export const createSessionId = () => {
  if (typeof window === 'undefined' || !window.crypto) {
    throw new Error('Web Crypto is required to create an isolated chat session');
  }

  if (typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'));

  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10, 16).join(''),
  ].join('-');
};

export const isValidSessionId = (value) =>
  typeof value === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value
  );

export const normalizeConversationState = (state, responseMode = '') => {
  const normalized = { ...EMPTY_CONVERSATION_STATE };
  const candidateMode =
    state && typeof state.previous_response_mode === 'string'
      ? state.previous_response_mode
      : responseMode;

  if (TRACKED_RESPONSE_MODES.has(candidateMode)) {
    normalized.previous_response_mode = candidateMode;
  }

  if (
    normalized.previous_response_mode === 'country_disambiguation' &&
    state &&
    typeof state.pending_country === 'string' &&
    state.pending_country.trim().length <= 80
  ) {
    normalized.pending_country = state.pending_country.trim();
  }

  if (state && Array.isArray(state.last_offer_ids)) {
    normalized.last_offer_ids = Array.from(
      new Set(
        state.last_offer_ids
          .map((id) => Number(id))
          .filter((id) => Number.isInteger(id) && id > 0)
      )
    ).slice(0, 8);
  }

  const selectedOfferId = Number(state?.selected_offer_id);

  if (
    Number.isInteger(selectedOfferId) &&
    selectedOfferId > 0 &&
    normalized.last_offer_ids.includes(selectedOfferId)
  ) {
    normalized.selected_offer_id = selectedOfferId;
  }

  if (INFORMATIONAL_RESPONSE_MODES.has(normalized.previous_response_mode)) {
    if (Array.isArray(state?.informational_aspects)) {
      normalized.informational_aspects = state.informational_aspects.filter(
        (aspect) => typeof aspect === 'string' && aspect.trim().length > 0 &&
          aspect.length <= 200 && !/https?:|[<>\r\n]/i.test(aspect)
      ).slice(0, 6);
    }
    if (
      state &&
      typeof state.informational_topic === 'string' &&
      INFORMATIONAL_TOPICS.has(state.informational_topic)
    ) {
      normalized.informational_topic = state.informational_topic;
    }

    if (state && Array.isArray(state.informational_countries)) {
      normalized.informational_countries = Array.from(
        new Set(
          state.informational_countries
            .filter((country) => typeof country === 'string')
            .map((country) => country.trim())
            .filter(
              (country) =>
                country.length > 0 &&
                country.length <= 80 &&
                /^[\p{L}\p{M} .'’\-]+$/u.test(country)
            )
        )
      ).slice(0, 3);
    }
  }

  return normalized;
};

export const normalizeFilters = (filters) => {
  if (!filters || typeof filters !== 'object') {
    return EMPTY_FILTERS;
  }

  return {
    intent:
      typeof filters.intent === 'string' &&
      ['stage_search', 'informational', 'unclear'].includes(filters.intent)
        ? filters.intent
        : 'unclear',
    paesi: Array.isArray(filters.paesi) ? filters.paesi : [],
    paesi_ids: Array.isArray(filters.paesi_ids) ? filters.paesi_ids : [],
    aree: Array.isArray(filters.aree) ? filters.aree : [],
    aree_ids: Array.isArray(filters.aree_ids) ? filters.aree_ids : [],
    keywords: Array.isArray(filters.keywords) ? filters.keywords : [],
    confidence:
      typeof filters.confidence === 'number' && Number.isFinite(filters.confidence)
        ? filters.confidence
        : 0,
  };
};

const isValidStoredMessages = (messages) =>
  Array.isArray(messages) &&
  messages.length > 0 &&
  messages.every((message) => {
    if (
      !message ||
      typeof message !== 'object' ||
      typeof message.id !== 'string' ||
      !['user', 'bot'].includes(message.role) ||
      !['text', 'offers', 'calls', 'skeleton'].includes(message.type)
    ) {
      return false;
    }

    if (message.type === 'text') {
      return typeof message.text === 'string';
    }

    if (message.type === 'offers') {
      return Array.isArray(message.offers);
    }

    if (message.type === 'calls') {
      return Array.isArray(message.calls);
    }

    return true;
  });

const normalizeMessages = (messages) =>
  messages.filter((message) => message.type !== 'skeleton');

export const getStoredChatState = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);

    if (
      !parsed ||
      typeof parsed !== 'object' ||
      parsed.version !== STORAGE_VERSION ||
      !isValidSessionId(parsed.sessionId) ||
      !isValidStoredMessages(parsed.messages)
    ) {
      window.sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }

    if (
      parsed.pendingRequest === true ||
      parsed.messages.some((message) => message.type === 'skeleton')
    ) {
      // Non sappiamo se AnythingLLM abbia già salvato il turno interrotto.
      window.sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return {
      version: STORAGE_VERSION,
      sessionId: parsed.sessionId.toLowerCase(),
      isOpen: Boolean(parsed.isOpen),
      messages: normalizeMessages(parsed.messages),
      activeFilters: normalizeFilters(parsed.activeFilters),
      conversationState: normalizeConversationState(parsed.conversationState),
    };
  } catch (error) {
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch (storageError) {
      // sessionStorage può essere completamente disabilitato dal browser.
    }

    console.warn('Unable to read chatbot session state', error);
    return null;
  }
};

export const saveChatState = (state) => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    return true;
  } catch (error) {
    console.warn('Unable to save chatbot session state', error);
    return false;
  }
};

export const clearChatState = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
    return true;
  } catch (error) {
    console.warn('Unable to clear chatbot session state', error);
    return false;
  }
};

export const buildChatRequestPayload = (
  message,
  sessionId,
  conversationState,
  activeFilters
) => ({
  message,
  session_id: sessionId,
  conversation_state: conversationState,
  active_filters: activeFilters,
});

export const isCurrentChatRequest = (
  requestGeneration,
  currentGeneration,
  requestSessionId,
  currentSessionId
) =>
  requestGeneration === currentGeneration &&
  requestSessionId === currentSessionId;

export const isDefinitiveChatClientError = (status) =>
  DEFINITIVE_CHAT_CLIENT_ERRORS.has(status);

export const shouldRotateForSessionClaim = (localInstance, remoteInstance) =>
  localInstance.startedAt > remoteInstance.startedAt ||
  (
    localInstance.startedAt === remoteInstance.startedAt &&
    localInstance.id > remoteInstance.id
  );
